import cx from "classnames";
import { t } from "ttag";

import { Button, type ButtonProps, Icon, Tooltip } from "metabase/ui";
import { ALTKEY, isMac } from "metabase/utils/browser";

import S from "./KeyboardFocusHint.module.css";

const ICON_SIZE = 18;

interface KeyboardFocusHintProps {
  className?: string;
  size?: ButtonProps["size"];
}

/**
 * WCAG 2.1.2 No Keyboard Trap: Tab and Shift-Tab always indent inside a
 * CodeMirror editor here, matching standard code-editor UX, so a keyboard
 * user needs a non-standard method to move focus out. CodeMirror provides
 * one (Escape then Tab within two seconds, or a key combo that toggles it
 * permanently) but never surfaces it anywhere in the UI on its own. WCAG
 * requires the user be advised of a non-standard method, so this renders a
 * real, focusable button whose accessible name carries the full advisory
 * (not just the hover/focus tooltip) for screen reader users too.
 */
export function KeyboardFocusHint({ className, size }: KeyboardFocusHintProps) {
  const toggleShortcut = isMac() ? `Shift+${ALTKEY}+M` : "Ctrl+M";
  const hintText = t`Tab and Shift-Tab indent code here instead of moving focus. To move focus out of the editor with the keyboard, press Escape then Tab, or press ${toggleShortcut} to turn off Tab indenting.`;

  return (
    <Tooltip label={hintText} multiline maw="16rem">
      <Button
        variant="subtle"
        className={cx(S.button, className)}
        aria-label={hintText}
        size={size}
        p={0}
        leftSection={<Icon name="info" size={ICON_SIZE} />}
      />
    </Tooltip>
  );
}
